import type { EventBus } from '@/core/EventBus';

/**
 * Economy events
 */
export const EconomyEvents = {
  GOLD_CHANGED: 'economy:goldChanged',
  PURCHASE_MADE: 'economy:purchase',
  INSUFFICIENT_FUNDS: 'economy:insufficientFunds',
  INCOME_RECEIVED: 'economy:income',
} as const;

export interface GoldChangedEvent {
  playerId: number;
  previousAmount: number;
  newAmount: number;
  reason: string;
}

/**
 * Economy configuration
 */
export interface EconomyConfig {
  startingGold: number;
  baseIncome: number;
  interestRate: number; // % per 10 gold
  maxInterest: number;
  winBonus: number;
  loseBonus: number;
  winStreakBonus: number[];
  loseStreakBonus: number[];
  rerollCost: number;
  unitSellMultiplier: number; // Get back this % of unit cost
}

export const DEFAULT_ECONOMY_CONFIG: EconomyConfig = {
  startingGold: 4,
  baseIncome: 4,
  interestRate: 1, // +1 gold per 10 gold saved
  maxInterest: 5, // Cap at +5 from interest
  winBonus: 1,
  loseBonus: 1,
  winStreakBonus: [0, 0, 1, 2, 3], // Index = streak count
  loseStreakBonus: [0, 0, 1, 2, 3],
  rerollCost: 1,
  unitSellMultiplier: 0.5, // Get back 50% of cost
};

/**
 * Player economy state
 */
interface PlayerEconomy {
  gold: number;
  winStreak: number;
  loseStreak: number;
  totalEarned: number;
  totalSpent: number;
}

/**
 * Manages gold and economy for both players
 */
export class CurrencyManager {
  private eventBus: EventBus;
  private config: EconomyConfig;

  private player1Economy: PlayerEconomy;
  private player2Economy: PlayerEconomy;

  constructor(eventBus: EventBus, config: EconomyConfig = DEFAULT_ECONOMY_CONFIG) {
    this.eventBus = eventBus;
    this.config = config;

    this.player1Economy = this.createInitialEconomy();
    this.player2Economy = this.createInitialEconomy();
  }

  /**
   * Create initial economy state
   */
  private createInitialEconomy(): PlayerEconomy {
    return {
      gold: this.config.startingGold,
      winStreak: 0,
      loseStreak: 0,
      totalEarned: this.config.startingGold,
      totalSpent: 0,
    };
  }

  /**
   * Get player economy
   */
  private getEconomy(playerId: number): PlayerEconomy {
    return playerId === 1 ? this.player1Economy : this.player2Economy;
  }

  /**
   * Get player's current gold
   */
  getGold(playerId: number): number {
    return this.getEconomy(playerId).gold;
  }

  /**
   * Set player's gold directly (for multiplayer sync)
   */
  setGold(playerId: number, amount: number): void {
    const economy = this.getEconomy(playerId);
    const previous = economy.gold;
    economy.gold = amount;

    this.eventBus.emit(EconomyEvents.GOLD_CHANGED, {
      playerId,
      previousAmount: previous,
      newAmount: economy.gold,
      reason: 'Server sync',
    } as GoldChangedEvent);
  }

  /**
   * Add gold to a player
   */
  addGold(playerId: number, amount: number, reason: string): void {
    const economy = this.getEconomy(playerId);
    const previous = economy.gold;
    economy.gold += amount;
    economy.totalEarned += amount;

    this.eventBus.emit(EconomyEvents.GOLD_CHANGED, {
      playerId,
      previousAmount: previous,
      newAmount: economy.gold,
      reason,
    } as GoldChangedEvent);
  }

  /**
   * Try to spend gold
   */
  spendGold(playerId: number, amount: number, reason: string): boolean {
    const economy = this.getEconomy(playerId);

    if (economy.gold < amount) {
      this.eventBus.emit(EconomyEvents.INSUFFICIENT_FUNDS, {
        playerId,
        required: amount,
        current: economy.gold,
        reason,
      });
      return false;
    }

    const previous = economy.gold;
    economy.gold -= amount;
    economy.totalSpent += amount;

    this.eventBus.emit(EconomyEvents.GOLD_CHANGED, {
      playerId,
      previousAmount: previous,
      newAmount: economy.gold,
      reason,
    } as GoldChangedEvent);

    this.eventBus.emit(EconomyEvents.PURCHASE_MADE, {
      playerId,
      amount,
      reason,
    });

    return true;
  }

  /**
   * Check if player can afford something
   */
  canAfford(playerId: number, amount: number): boolean {
    return this.getGold(playerId) >= amount;
  }

  /**
   * Calculate income for round end
   */
  calculateIncome(playerId: number): number {
    const economy = this.getEconomy(playerId);

    let income = this.config.baseIncome;

    // Interest (1 gold per 10 saved, up to cap)
    const interest = Math.min(
      Math.floor(economy.gold / 10) * this.config.interestRate,
      this.config.maxInterest
    );
    income += interest;

    // Win/lose streak bonus
    if (economy.winStreak > 0) {
      const streakIndex = Math.min(economy.winStreak, this.config.winStreakBonus.length - 1);
      income += this.config.winStreakBonus[streakIndex];
    } else if (economy.loseStreak > 0) {
      const streakIndex = Math.min(economy.loseStreak, this.config.loseStreakBonus.length - 1);
      income += this.config.loseStreakBonus[streakIndex];
    }

    return income;
  }

  /**
   * Process round result and give income
   */
  processRoundResult(winnerId: number): void {
    // Update streaks
    if (winnerId === 1) {
      this.player1Economy.winStreak++;
      this.player1Economy.loseStreak = 0;
      this.player2Economy.loseStreak++;
      this.player2Economy.winStreak = 0;
    } else if (winnerId === 2) {
      this.player2Economy.winStreak++;
      this.player2Economy.loseStreak = 0;
      this.player1Economy.loseStreak++;
      this.player1Economy.winStreak = 0;
    } else {
      // Draw - reset both streaks
      this.player1Economy.winStreak = 0;
      this.player1Economy.loseStreak = 0;
      this.player2Economy.winStreak = 0;
      this.player2Economy.loseStreak = 0;
    }

    // Give income to both players
    for (const playerId of [1, 2]) {
      let income = this.calculateIncome(playerId);

      // Win/lose bonus
      if (playerId === winnerId) {
        income += this.config.winBonus;
      } else if (winnerId !== 0) {
        income += this.config.loseBonus;
      }

      this.addGold(playerId, income, 'Round income');

      this.eventBus.emit(EconomyEvents.INCOME_RECEIVED, {
        playerId,
        income,
        breakdown: {
          base: this.config.baseIncome,
          interest: Math.min(
            Math.floor(this.getGold(playerId) / 10),
            this.config.maxInterest
          ),
          streak: this.calculateStreakBonus(playerId),
          winLose: playerId === winnerId ? this.config.winBonus : this.config.loseBonus,
        },
      });
    }
  }

  /**
   * Calculate streak bonus
   */
  private calculateStreakBonus(playerId: number): number {
    const economy = this.getEconomy(playerId);

    if (economy.winStreak > 0) {
      const index = Math.min(economy.winStreak, this.config.winStreakBonus.length - 1);
      return this.config.winStreakBonus[index];
    } else if (economy.loseStreak > 0) {
      const index = Math.min(economy.loseStreak, this.config.loseStreakBonus.length - 1);
      return this.config.loseStreakBonus[index];
    }
    return 0;
  }

  /**
   * Get reroll cost
   */
  getRerollCost(): number {
    return this.config.rerollCost;
  }

  /**
   * Get sell value for a unit
   */
  getSellValue(baseCost: number): number {
    return Math.floor(baseCost * this.config.unitSellMultiplier);
  }

  /**
   * Get player's streak info
   */
  getStreak(playerId: number): { winStreak: number; loseStreak: number } {
    const economy = this.getEconomy(playerId);
    return {
      winStreak: economy.winStreak,
      loseStreak: economy.loseStreak,
    };
  }

  /**
   * Get player's total stats
   */
  getStats(playerId: number): { totalEarned: number; totalSpent: number } {
    const economy = this.getEconomy(playerId);
    return {
      totalEarned: economy.totalEarned,
      totalSpent: economy.totalSpent,
    };
  }

  /**
   * Reset economy for new match
   */
  reset(): void {
    this.player1Economy = this.createInitialEconomy();
    this.player2Economy = this.createInitialEconomy();
  }
}
